import React from 'react';
import config from '../../../config';
import routeConfiguration from '../../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../../util/routes';
import { findConfigForSelectFilter } from '../../../util/search';
import {
  convertArrayToSearchString,
  convertMultipleSelectQueryStringToArray,
} from './utils';
import css from './SearchChipsSection.module.css';

const SearchChipsSection = ({
  currentQueryParams,
  history,
  byRoomSearch,
  byProductSearch,
  byBrandSearch,
  byEraSearch,
  isCategorySearch,
  setAsideImage,
  currentCategory,
  handleCategoryChanged,
  intl,
}) => {
  const clearLabel = intl.formatMessage({ id: 'SearchChipSection.Clear' });

  const handleFiltersChange = newValues => {
    history.push(
      createResourceLocatorString(
        isCategorySearch ? 'SearchPageWithCategories' : 'SearchPage',
        routeConfiguration(),
        {},
        { ...currentQueryParams, ...newValues }
      )
    );
  };

  const getFilterConfig = key => {
    const configData = findConfigForSelectFilter(key, config.custom.filters);
    return configData?.options || [];
  };

  const roomOptions = getFilterConfig('room');
  const brandOptions = getFilterConfig('brand');
  const eraOptions = getFilterConfig('era');
  const brandCategories = ['A - F', 'G - M', 'N - T', 'U - Z'];

  const productOptions = (() => {
    const options = getFilterConfig('productType');
    const grouped = [...new Set(options.map(i => i.category))].sort();

    return grouped.map(category => ({
      key: category,
      label: category,
      category,
      options: options.filter(opt => opt.category === category),
    }));
  })();

  const chipsOptions = (() => {
    if (byRoomSearch) return roomOptions;
    if (byProductSearch) return productOptions;
    if (byEraSearch) return eraOptions;
    if (byBrandSearch)
      return currentCategory
        ? brandOptions.filter(b => b.category === currentCategory)
        : brandOptions;
    return [];
  })();

  const keySearchLabel = byRoomSearch
    ? 'pub_room'
    : byProductSearch
      ? 'pub_productType'
      : byBrandSearch
        ? 'pub_brand'
        : byEraSearch
          ? 'pub_era' : '';

  const selectedKeys = convertMultipleSelectQueryStringToArray(
    currentQueryParams?.[keySearchLabel]
  ) || [];

  const selectedChips = selectedKeys.map(key => ({
    key,
    label: chipsOptions.find(opt => opt.key === key)?.label,
  }));

  const handleClearAll = () => {
    handleFiltersChange({ [keySearchLabel]: '' });
    setAsideImage('Interiors');
  };

  const handleChipToggle = chip => {
    if (!chip) return;

    const isSelected = byProductSearch
      ? chip.options.some(opt => selectedKeys.includes(opt.key))
      : selectedKeys.includes(chip.key);

    if (isSelected) {
      // Deselect
      handleFiltersChange({ [keySearchLabel]: '' });
      setAsideImage('Interiors');
    } else {
      // Single select
      const newKeys = byProductSearch
        ? chip.options.map(opt => opt.key)
        : [chip.key];

      const queryValue = convertArrayToSearchString(newKeys, 'has_any');
      handleFiltersChange({ [keySearchLabel]: queryValue });
      setAsideImage(chip.key);
    }
  };


  const renderChip = chip => {
    const isSelected = byProductSearch
      ? chip.options.some(opt => selectedKeys.includes(opt.key))
      : selectedKeys.includes(chip.key);

    return (
      <div
        key={chip.key}
        className={isSelected ? css.chipWrapperSelected : css.chipWrapperNotSelected}
        onClick={() => handleChipToggle(chip)}
      >
        {chip.label}
      </div>
    );
  };

  const renderBrandCategories = () =>
    brandCategories.map(category => {
      const isSelected = category === currentCategory;
      return (
        <div
          key={category}
          className={isSelected ? css.chipWrapperSelected : css.chipWrapperNotSelected}
          onClick={() => handleCategoryChanged(category)}
        >
          {category}
        </div>
      );
    });

  return (
    <div className={css.stickyWrapper}>
      {/* Optional: brand categories filter */}
      {byBrandSearch && <div className={css.wrapper}>{renderBrandCategories()}</div>}

      {!(byBrandSearch && !currentCategory) && (
        <div className={css.wrapper}>
          {chipsOptions.map(renderChip)}
          <div className={css.resetButtonWrapper}>
            <div className={css.button} onClick={handleClearAll}>
              {clearLabel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchChipsSection;
